import { useEffect, useState } from 'react'
import { dequal } from 'dequal'
import debounce from 'lodash/debounce'
import { useSelector } from 'react-redux'
import { config } from '@config/config'
import { ProductType } from '@typesApp/Product'
import { useEnvironment } from '@hooks/useEnvironment'
import { useClaimRecord } from '@hooks/useClaimRecord'
import { useToggleWidgetContext } from '@components/widgetContext/useWidgetContext'
import { useGetEligibleProductTypesMutation } from '@redux/queries/claimRecords'
import {
  authTokenErrorSelector,
  authTokenReadySelector,
  connectorProductQuoteErrorSelector,
} from '@redux/slices/connectorSlice'
import {
  getInsuranceToggleEligibilityMessageKey,
  getInsuranceToggleMessageKey,
  getInsuranceToggleMessageKeyForCart,
  hasEligibleProductType,
} from '@utils/insuranceToggleUtil'
import { isInsuranceMemberFoundStatus, isInsuranceUsageEnabled } from '@utils/insuranceMemberUtil'

type UseInsuranceToggleMessageReturn = {
  isLoading?: boolean
  messageKey?: string
}

export const useInsuranceToggleMessage = (): UseInsuranceToggleMessageReturn => {
  const [lastProductTypes, setLastProductTypes] = useState<ProductType[] | undefined>()
  const { cartPath } = useEnvironment()
  const tokenReady = useSelector(authTokenReadySelector)
  const tokenError = useSelector(authTokenErrorSelector)
  const productQuoteError = useSelector(connectorProductQuoteErrorSelector)

  const [isLoading, setLoading] = useState(false)
  const [messageKey, setMessageKey] = useState<string | undefined>()
  const { claimRecord, claimRecordDeleted, isLoading: isLoadingClaimRecord } = useClaimRecord()
  const [triggerProductTypesCheck] = useGetEligibleProductTypesMutation()

  const context = useToggleWidgetContext()
  const { enableMessages, productTypes } = context ?? {}

  useEffect(() => {
    const disabled = config.getFeatures()?.disableInsuranceToggleMessages
    if (disabled || claimRecordDeleted || isLoadingClaimRecord) return

    // always show messages on cart
    const isCart = window.location.pathname.includes(cartPath)
    if (isCart) {
      setMessageKey(getInsuranceToggleMessageKeyForCart({ claimRecord, claimRecordDeleted, initError: tokenError }))
      return
    }

    // non cart page scenarios
    setMessageKey(undefined)

    // no messages
    if (!enableMessages) {
      return
    }

    // default message (e.g. no benefits or product quote error)
    const defaultMessageKey = getInsuranceToggleMessageKey({ claimRecord, claimRecordDeleted, productQuoteError })
    if (defaultMessageKey) {
      setMessageKey(defaultMessageKey)
      return
    }

    if (shouldCheckProductEligibilty()) {
      setEligibilityMessageKey()
    }
  }, [claimRecord, claimRecordDeleted, tokenReady, tokenError, productQuoteError, enableMessages, productTypes])

  const shouldCheckProductEligibilty = (): boolean => {
    if (!tokenReady) return false
    if (!Array.isArray(productTypes) || !productTypes.length) return false

    const validProductTypes = productTypes.filter(type => type)
    if (!validProductTypes.length) return false

    if (dequal(validProductTypes, lastProductTypes)) return false

    // skip if not synced and not ON
    return (
      isInsuranceMemberFoundStatus(claimRecord?.status) &&
      isInsuranceUsageEnabled(claimRecord?.claimData?.useInsuranceStatus)
    )
  }

  const setEligibilityMessageKey = debounce(async (): Promise<void> => {
    const validProductTypes = (productTypes ?? []).filter(type => type)

    setLoading(true)
    try {
      const result = await triggerProductTypesCheck(validProductTypes).unwrap()
      const hasEligibleProduct = hasEligibleProductType(result)
      const key = getInsuranceToggleEligibilityMessageKey({ hasEligibleProduct, claimRecord, claimRecordDeleted })
      setMessageKey(key)
      setLastProductTypes(validProductTypes)
    } catch (error) {
      console.warn('Unable to check product type eligibility', error)
    } finally {
      setLoading(false)
    }
  })

  return {
    isLoading: isLoading || isLoadingClaimRecord,
    messageKey,
  }
}
